/* eslint-disable react/prop-types */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import {
  Steps,
  Button,
  Select,
  message,
  Checkbox,
  Card,
  Row,
  Col,
  Alert,
  Icon,
  Spin,
  Table,
  Tabs,
  Modal
} from 'antd';
import moment from 'moment';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { get, post } from '../utils/httpRequests';
import { APIs } from '../utils/apis';

const { Step } = Steps;
const { Option } = Select;
const { TabPane } = Tabs;
const { confirm } = Modal;

const SERVICE_ID = 137;

const steps = [
  { title: 'Prerequisite' },
  { title: 'Template migration' },
  { title: 'Journey migration' },
  { title: 'Complete' }
];

const formatMs = ms =>
  ms != null && !Number.isNaN(Number(ms))
    ? moment(Number(ms)).format('Do MMM YYYY h:mm a')
    : '—';

const STATUS_ORDER = { error: 0, pending: 1, idle: 2, success: 3 };

const migrationLogColumns = [
  {
    title: 'Enterprise',
    dataIndex: 'enterpriseName',
    key: 'enterpriseName',
    width: 180,
    sorter: (a, b) =>
      String(a.enterpriseName || '').localeCompare(
        String(b.enterpriseName || '')
      ),
    render: (text, row) => text || `— (ID: ${row.enterpriseId})`
  },
  {
    title: 'Enterprise ID',
    dataIndex: 'enterpriseId',
    key: 'enterpriseId',
    width: 110,
    sorter: (a, b) => Number(a.enterpriseId) - Number(b.enterpriseId)
  },
  {
    title: 'Template migrated',
    dataIndex: 'templateMigratedAt',
    key: 'templateMigratedAt',
    width: 170,
    sorter: (a, b) =>
      Number(a.templateMigratedAt || 0) - Number(b.templateMigratedAt || 0),
    render: ms => formatMs(ms)
  },
  {
    title: 'Journey migrated',
    dataIndex: 'journeyMigratedAt',
    key: 'journeyMigratedAt',
    width: 170,
    sorter: (a, b) =>
      Number(a.journeyMigratedAt || 0) - Number(b.journeyMigratedAt || 0),
    render: ms => formatMs(ms)
  },
  {
    title: 'Template step',
    dataIndex: 'templateMigrationStatus',
    key: 'templateMigrationStatus',
    width: 120,
    sorter: (a, b) =>
      String(a.templateMigrationStatus || '').localeCompare(
        String(b.templateMigrationStatus || '')
      ),
    render: s =>
      ({
        success: 'Succeeded',
        error: 'Failed',
        pending: 'Running',
        idle: '—'
      }[s] ||
      s ||
      '—')
  },
  {
    title: 'Journey step',
    dataIndex: 'journeyMigrationStatus',
    key: 'journeyMigrationStatus',
    width: 120,
    sorter: (a, b) =>
      String(a.journeyMigrationStatus || '').localeCompare(
        String(b.journeyMigrationStatus || '')
      ),
    render: s =>
      ({
        success: 'Succeeded',
        error: 'Failed',
        pending: 'Running',
        idle: '—'
      }[s] ||
      s ||
      '—')
  },
  {
    title: 'Migrated by',
    dataIndex: 'migratedByName',
    key: 'migratedByName',
    width: 140,
    sorter: (a, b) =>
      String(a.migratedByName || '').localeCompare(
        String(b.migratedByName || '')
      ),
    render: (text, row) => text || row.migratedByEmail || '—'
  }
  // {
  //   title: 'Email',
  //   dataIndex: 'migratedByEmail',
  //   key: 'migratedByEmail',
  //   ellipsis: true,
  //   sorter: (a, b) =>
  //     String(a.migratedByEmail || '').localeCompare(
  //       String(b.migratedByEmail || '')
  //     )
  // }
];

const emptyMigrationRows = ids =>
  ids.map(id => ({
    enterpriseId: id,
    enterpriseName: '',
    status: 'idle',
    errorMessage: null,
    migratedAt: null
  }));

const emptyJourneyRows = ids =>
  ids.map(id => ({
    enterpriseId: id,
    enterpriseName: '',
    status: 'idle',
    errorMessage: null,
    migratedAt: null
  }));

const isTemplateResponseOk = res => {
  if (!res || res.status >= 400) return false;
  if (res.status === 200 && res.data?.meta?.status === 200) return true;
  if (res.status === 200 && res.data?.meta?.status == null) return true;
  return false;
};

const templateErrorMessage = res =>
  res?.data?.meta?.message || res?.data?.message || 'Template migration failed';

const isJourneyResponseOk = res => {
  if (!res || res.status >= 400) return false;
  if (res.status === 200 && res.data?.meta?.status === 200) return true;
  return false;
};

const journeyErrorMessage = res =>
  res?.data?.meta?.message || res?.data?.message || 'Journey migration failed';

const parseLogResponseBody = logRes => {
  const raw = logRes?.data;
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

/** Resolve per-enterprise API token (same flow as Simulation / Enterprise Login). */
const fetchEnterpriseSubToken = async enterpriseId => {
  const loginRes = await get({
    url: `${APIs.DASHBOARD_LOGIN}&enterprise_id=${enterpriseId}`
  });
  if (!loginRes?.data?.result?.length) return null;
  const userObj = loginRes.data.result[0];
  if (userObj.sub_token) return userObj.sub_token;
  const cookies = new Cookies();
  const internalUserId = cookies.get('user_id');
  try {
    const tokenRes = await axios({
      url: APIs.GENERATE_TOKEN,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: {
        user_id: userObj.user_id,
        internal_user_id: internalUserId
      }
    });
    if (tokenRes?.data?.token) return tokenRes.data.token;
  } catch {
    /* ignore */
  }
  return null;
};

const sendMigrationHistoryLog = async ({
  enterpriseId,
  enterpriseName,
  templateMigratedAt,
  journeyMigratedAt,
  completedAt,
  templateMigrationStatus,
  journeyMigrationStatus
}) => {
  const logRes = await post({
    url: APIs.NOTIFICATIONS_SMART_MIGRATION_LOGS,
    data: {
      enterpriseId,
      enterpriseName: enterpriseName || '',
      templateMigratedAt:
        templateMigratedAt != null ? Number(templateMigratedAt) : null,
      journeyMigratedAt:
        journeyMigratedAt != null ? Number(journeyMigratedAt) : null,
      completedAt: completedAt != null ? Number(completedAt) : Date.now(),
      templateMigrationStatus: templateMigrationStatus || '',
      journeyMigrationStatus: journeyMigrationStatus || ''
    }
  });
  const body = parseLogResponseBody(logRes);
  const ok = logRes && logRes.status < 400 && body?.meta?.status === 200;
  return ok;
};

const NotificationsMigration = () => {
  const [current, setCurrent] = useState(0);
  const [enterprise, setEnterprise] = useState({
    list: [],
    isFetching: true
  });
  const [selectedEnterpriseIds, setSelectedEnterpriseIds] = useState([]);
  const [prerequisiteAck, setPrerequisiteAck] = useState(false);
  const [templateRows, setTemplateRows] = useState([]);
  const [journeyRows, setJourneyRows] = useState([]);
  const [templateBatchLoading, setTemplateBatchLoading] = useState(false);
  const [journeyBatchLoading, setJourneyBatchLoading] = useState(false);
  const [retryingTemplateId, setRetryingTemplateId] = useState(null);
  const [retryingJourneyId, setRetryingJourneyId] = useState(null);
  const [finishPersistLoading, setFinishPersistLoading] = useState(false);
  const [finishLogResultById, setFinishLogResultById] = useState({});
  const [migrationLogs, setMigrationLogs] = useState({
    list: [],
    isFetching: true
  });
  const [activeTab, setActiveTab] = useState('migrate');

  const templateRowsRef = useRef(templateRows);
  const journeyRowsRef = useRef(journeyRows);
  const selectedEnterpriseIdsRef = useRef(selectedEnterpriseIds);
  templateRowsRef.current = templateRows;
  journeyRowsRef.current = journeyRows;
  selectedEnterpriseIdsRef.current = selectedEnterpriseIds;

  const enterpriseById = useMemo(() => {
    const m = new Map();
    enterprise.list.forEach(e => m.set(e.id, e));
    return m;
  }, [enterprise.list]);

  const enterpriseByIdRef = useRef(enterpriseById);
  enterpriseByIdRef.current = enterpriseById;

  const selectedEnterprises = useMemo(
    () =>
      selectedEnterpriseIds.map(id => enterpriseById.get(id)).filter(Boolean),
    [selectedEnterpriseIds, enterpriseById]
  );

  const fetchMigrationLogs = useCallback(() => {
    setMigrationLogs(prev => ({ ...prev, isFetching: true }));
    get({ url: APIs.NOTIFICATIONS_SMART_MIGRATION_LOGS })
      .then(res => {
        if (res?.data?.meta?.status === 200) {
          const list = Array.isArray(res.data.result?.list)
            ? res.data.result.list
            : [];
          setMigrationLogs({ list, isFetching: false });
        } else {
          setMigrationLogs(prev => ({ ...prev, isFetching: false }));
        }
      })
      .catch(() => {
        setMigrationLogs(prev => ({ ...prev, isFetching: false }));
      });
  }, []);

  useEffect(() => {
    const fetchAccess = get({ url: APIs.USER.SELF_ACCESS });
    const fetchEnterprises = get({ url: APIs.USER.ENTERPRISES });

    Promise.all([fetchAccess, fetchEnterprises])
      .then(([accessRes, entRes]) => {
        let enterprises = [];
        if (entRes?.data?.meta?.status === 200) {
          enterprises = entRes.data.result.list;
        }

        if (accessRes?.data?.meta?.status === 200) {
          const { accessToAll, enterpriseIds } =
            accessRes.data.result.list[0] || {};
          if (!accessToAll) {
            const allowed = enterpriseIds || [];
            enterprises = enterprises.filter(ent =>
              allowed.some(aid => Number(aid) === Number(ent.id))
            );
          }
        }

        setEnterprise({
          list: enterprises
            .sort((a, b) => a?.name?.localeCompare(b.name))
            .filter(e => e.is_active),
          isFetching: false
        });
      })
      .catch(() => {
        setEnterprise({
          list: [],
          isFetching: false
        });
      });
  }, []);

  useEffect(() => {
    fetchMigrationLogs();
  }, [fetchMigrationLogs]);

  const syncRowNames = rows =>
    rows.map(r => ({
      ...r,
      enterpriseName:
        enterpriseById.get(r.enterpriseId)?.name ||
        r.enterpriseName ||
        String(r.enterpriseId)
    }));

  useEffect(() => {
    if (!enterpriseById.size) return;
    setTemplateRows(prev => syncRowNames(prev));
    setJourneyRows(prev => syncRowNames(prev));
  }, [enterpriseById, selectedEnterpriseIds]);

  const handleEnterpriseChange = values => {
    const ids = Array.isArray(values) ? values : values ? [values] : [];
    setSelectedEnterpriseIds(ids);
    setPrerequisiteAck(false);
    setTemplateRows(emptyMigrationRows(ids));
    setJourneyRows(emptyJourneyRows(ids));
  };

  const prev = () => {
    setCurrent(c => Math.max(0, c - 1));
  };

  const nextFromPrerequisite = () => {
    if (!selectedEnterpriseIds.length) {
      message.warning('Select at least one enterprise');
      return;
    }
    if (!prerequisiteAck) {
      message.warning(
        `Confirm that service ID ${SERVICE_ID} is configured in Django admin for every selected enterprise`
      );
      return;
    }
    setTemplateRows(
      selectedEnterpriseIds.map(id => ({
        enterpriseId: id,
        enterpriseName: enterpriseById.get(id)?.name || String(id),
        status: 'idle',
        errorMessage: null,
        migratedAt: null
      }))
    );
    setJourneyRows(
      selectedEnterpriseIds.map(id => ({
        enterpriseId: id,
        enterpriseName: enterpriseById.get(id)?.name || String(id),
        status: 'idle',
        errorMessage: null,
        migratedAt: null
      }))
    );
    setCurrent(1);
  };

  const runTemplateForEnterprise = async enterpriseId => {
    const enterpriseToken = await fetchEnterpriseSubToken(enterpriseId);
    if (!enterpriseToken) {
      return {
        ok: false,
        errMsg:
          'Could not resolve enterprise token — check dashboard users for this enterprise',
        migratedAt: null
      };
    }
    const res = await post({
      url: `${APIs.SMART_COMMUNICATION.TEMPLATE_MIGRATION}?enterprise_id=${enterpriseId}`,
      data: '',
      noToken: true,
      headers: {
        Authorization: `Token ${enterpriseToken}`
      }
    });
    const ok = isTemplateResponseOk(res);
    const errMsg = ok ? null : templateErrorMessage(res);
    const migratedAt = ok ? Date.now() : null;
    return { ok, errMsg, migratedAt };
  };

  const updateTemplateRow = (enterpriseId, patch) => {
    setTemplateRows(prev =>
      prev.map(r => (r.enterpriseId === enterpriseId ? { ...r, ...patch } : r))
    );
  };

  const runTemplateMigrationAll = async () => {
    if (!selectedEnterpriseIds.length) return;
    setTemplateBatchLoading(true);
    let okCount = 0;
    for (const id of selectedEnterpriseIds) {
      updateTemplateRow(id, { status: 'pending', errorMessage: null });
      try {
        const { ok, errMsg, migratedAt } = await runTemplateForEnterprise(id);
        if (ok) {
          okCount += 1;
          updateTemplateRow(id, {
            status: 'success',
            errorMessage: null,
            migratedAt
          });
        } else {
          updateTemplateRow(id, {
            status: 'error',
            errorMessage: errMsg,
            migratedAt: null
          });
        }
      } catch {
        updateTemplateRow(id, {
          status: 'error',
          errorMessage: 'Template migration request failed (network)',
          migratedAt: null
        });
      }
    }
    setTemplateBatchLoading(false);
    const total = selectedEnterpriseIds.length;
    if (okCount === total) {
      message.success('Template migration completed for all enterprises');
    } else if (okCount > 0) {
      message.warning(
        'Template migration finished with some failures — use Retry or sort by status to fix failed rows'
      );
    } else {
      message.error(
        'Template migration failed for all enterprises — fix issues and use Retry'
      );
    }
  };

  const retryTemplateMigration = async enterpriseId => {
    setRetryingTemplateId(enterpriseId);
    updateTemplateRow(enterpriseId, { status: 'pending', errorMessage: null });
    try {
      const { ok, errMsg, migratedAt } = await runTemplateForEnterprise(
        enterpriseId
      );
      if (ok) {
        updateTemplateRow(enterpriseId, {
          status: 'success',
          errorMessage: null,
          migratedAt
        });
        message.success(
          `Template migration succeeded for enterprise ${enterpriseId}`
        );
      } else {
        updateTemplateRow(enterpriseId, {
          status: 'error',
          errorMessage: errMsg,
          migratedAt: null
        });
        message.error(errMsg);
      }
    } catch {
      updateTemplateRow(enterpriseId, {
        status: 'error',
        errorMessage: 'Template migration request failed (network)',
        migratedAt: null
      });
      message.error('Template migration request failed');
    }
    setRetryingTemplateId(null);
  };

  const templateAllSucceeded = useMemo(
    () =>
      templateRows.length > 0 &&
      templateRows.every(r => r.status === 'success'),
    [templateRows]
  );

  const templateHasRun = useMemo(
    () =>
      templateRows.some(r => r.status === 'success' || r.status === 'error'),
    [templateRows]
  );

  const templateHasFailures = useMemo(
    () => templateRows.some(r => r.status === 'error'),
    [templateRows]
  );

  const goNextFromTemplate = () => {
    if (!templateHasRun) {
      message.warning('Run template migration at least once before continuing');
      return;
    }
    if (templateHasFailures) {
      confirm({
        title: 'Some template migrations failed',
        content:
          'You can retry failed rows here, or continue to journey migration and retry later. Journey migration may fail for enterprises without a successful template run.',
        okText: 'Continue to journey migration',
        cancelText: 'Stay on this step',
        onOk: () => setCurrent(2)
      });
      return;
    }
    setCurrent(2);
  };

  const writeBatchMigrationHistoryOnFinish = useCallback(
    async ({ silent } = {}) => {
      const ids = selectedEnterpriseIdsRef.current;
      const templates = templateRowsRef.current;
      const journeys = journeyRowsRef.current;
      const batchCompletedAt = Date.now();
      let saved = 0;
      let failed = 0;
      const results = {};

      for (const id of ids) {
        const tr = templates.find(r => r.enterpriseId === id);
        const jr = journeys.find(r => r.enterpriseId === id);
        const name =
          enterpriseByIdRef.current.get(id)?.name ||
          tr?.enterpriseName ||
          String(id);
        const templateMs = tr?.status === 'success' ? tr.migratedAt : null;
        const journeyMs = jr?.status === 'success' ? jr.migratedAt : null;

        const ok = await sendMigrationHistoryLog({
          enterpriseId: id,
          enterpriseName: name,
          templateMigratedAt: templateMs,
          journeyMigratedAt: journeyMs,
          completedAt: batchCompletedAt,
          templateMigrationStatus: tr?.status || 'idle',
          journeyMigrationStatus: jr?.status || 'idle'
        });

        results[id] = ok;
        if (ok) saved += 1;
        else failed += 1;
      }

      fetchMigrationLogs();

      if (!silent) {
        if (failed && !saved) {
          message.error(
            'Could not write migration history. Check your connection and click Finish again.'
          );
        } else if (failed) {
          message.warning(
            `Migration history: ${saved} row(s) saved, ${failed} failed. Click Finish again to append another log entry, or fix the issue first.`
          );
        } else if (saved > 0) {
          message.success(
            `Migration history saved for ${saved} enterprise${
              saved === 1 ? '' : 's'
            }.`
          );
        }
      }

      return { saved, failed, results };
    },
    [fetchMigrationLogs]
  );

  const runJourneyForEnterprise = async enterpriseId => {
    const enterpriseToken = await fetchEnterpriseSubToken(enterpriseId);
    if (!enterpriseToken) {
      return {
        ok: false,
        errMsg:
          'Could not resolve enterprise token — check dashboard users for this enterprise'
      };
    }
    const res = await post({
      url: APIs.SMART_COMMUNICATION.JOURNEY_MIGRATION,
      data: { enterprise_ids: [enterpriseId] },
      noToken: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${enterpriseToken}`
      }
    });
    const ok = isJourneyResponseOk(res);
    const errMsg = ok ? null : journeyErrorMessage(res);
    return { ok, errMsg };
  };

  const updateJourneyRow = (enterpriseId, patch) => {
    setJourneyRows(prev =>
      prev.map(r => (r.enterpriseId === enterpriseId ? { ...r, ...patch } : r))
    );
  };

  const runJourneyMigrationAll = async () => {
    if (!selectedEnterpriseIds.length) return;
    setJourneyBatchLoading(true);
    for (const id of selectedEnterpriseIds) {
      updateJourneyRow(id, { status: 'pending', errorMessage: null });
      try {
        const { ok, errMsg } = await runJourneyForEnterprise(id);
        if (ok) {
          const journeyMs = Date.now();
          updateJourneyRow(id, {
            status: 'success',
            errorMessage: null,
            migratedAt: journeyMs
          });
          journeyRowsRef.current = journeyRowsRef.current.map(r =>
            r.enterpriseId === id
              ? {
                  ...r,
                  status: 'success',
                  errorMessage: null,
                  migratedAt: journeyMs
                }
              : r
          );
        } else {
          updateJourneyRow(id, {
            status: 'error',
            errorMessage: errMsg,
            migratedAt: null
          });
        }
      } catch {
        updateJourneyRow(id, {
          status: 'error',
          errorMessage: 'Journey migration request failed (network)',
          migratedAt: null
        });
      }
    }
    setJourneyBatchLoading(false);
    fetchMigrationLogs();
    message.info(
      'Journey migration batch finished — check the table for per-enterprise status'
    );
  };

  const retryJourneyMigration = async enterpriseId => {
    setRetryingJourneyId(enterpriseId);
    updateJourneyRow(enterpriseId, { status: 'pending', errorMessage: null });
    try {
      const { ok, errMsg } = await runJourneyForEnterprise(enterpriseId);
      if (ok) {
        const journeyMs = Date.now();
        updateJourneyRow(enterpriseId, {
          status: 'success',
          errorMessage: null,
          migratedAt: journeyMs
        });
        journeyRowsRef.current = journeyRowsRef.current.map(r =>
          r.enterpriseId === enterpriseId
            ? {
                ...r,
                status: 'success',
                errorMessage: null,
                migratedAt: journeyMs
              }
            : r
        );
        message.success(
          `Journey migration succeeded for enterprise ${enterpriseId}`
        );
      } else {
        updateJourneyRow(enterpriseId, {
          status: 'error',
          errorMessage: errMsg,
          migratedAt: null
        });
        message.error(errMsg);
      }
    } catch {
      updateJourneyRow(enterpriseId, {
        status: 'error',
        errorMessage: 'Journey migration request failed (network)',
        migratedAt: null
      });
      message.error('Journey migration request failed');
    }
    setRetryingJourneyId(null);
  };

  const journeyAllSucceeded = useMemo(
    () =>
      journeyRows.length > 0 && journeyRows.every(r => r.status === 'success'),
    [journeyRows]
  );

  const journeyHasRun = useMemo(
    () => journeyRows.some(r => r.status === 'success' || r.status === 'error'),
    [journeyRows]
  );

  const journeyHasFailures = useMemo(
    () => journeyRows.some(r => r.status === 'error'),
    [journeyRows]
  );

  const proceedToCompletion = async () => {
    setFinishPersistLoading(true);
    try {
      const { results } = await writeBatchMigrationHistoryOnFinish({
        silent: false
      });
      setFinishLogResultById(results);
    } finally {
      setFinishPersistLoading(false);
    }
    setCurrent(3);
  };

  const goFinishFromJourney = () => {
    if (!journeyHasRun) {
      message.warning('Run journey migration at least once before finishing');
      return;
    }
    if (journeyHasFailures) {
      confirm({
        title: 'Some journey migrations failed',
        content:
          'Clicking Finish writes one migration history row per enterprise with the current template and journey outcomes (including failures). You can retry journey migration later if needed.',
        okText: 'Finish anyway',
        cancelText: 'Stay on this step',
        onOk: () => proceedToCompletion()
      });
      return;
    }
    proceedToCompletion();
  };

  const enterpriseSummaryLabel =
    selectedEnterprises.length === 0
      ? null
      : selectedEnterprises.length === 1
      ? `${selectedEnterprises[0].name} (ID: ${selectedEnterprises[0].id})`
      : `${selectedEnterprises.length} enterprises selected`;

  const migrationStatusTag = status => {
    if (status === 'success') {
      return <span style={{ color: '#52c41a' }}>Success</span>;
    }
    if (status === 'error') {
      return <span style={{ color: '#f5222d' }}>Failed</span>;
    }
    if (status === 'pending') {
      return <span style={{ color: '#1890ff' }}>Running…</span>;
    }
    return <span style={{ color: 'rgba(0,0,0,0.45)' }}>Not run</span>;
  };

  const templateTableColumns = [
    {
      title: 'Enterprise',
      dataIndex: 'enterpriseName',
      key: 'enterpriseName',
      sorter: (a, b) =>
        String(a.enterpriseName || '').localeCompare(
          String(b.enterpriseName || '')
        )
    },
    {
      title: 'Enterprise ID',
      dataIndex: 'enterpriseId',
      key: 'enterpriseId',
      width: 120,
      sorter: (a, b) => Number(a.enterpriseId) - Number(b.enterpriseId)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      sorter: (a, b) =>
        (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9),
      render: s => migrationStatusTag(s)
    },
    {
      title: 'Migrated at',
      dataIndex: 'migratedAt',
      key: 'migratedAt',
      width: 180,
      sorter: (a, b) => Number(a.migratedAt || 0) - Number(b.migratedAt || 0),
      render: ms => formatMs(ms)
    },
    {
      title: 'Details',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      ellipsis: true,
      render: function renderTemplateDetails(text, row) {
        if (row.status === 'error' && text) {
          return <span style={{ color: '#f5222d' }}>{text}</span>;
        }
        return '—';
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: function renderTemplateActions(_, row) {
        if (row.status !== 'error') {
          return '—';
        }
        return (
          <Button
            size="small"
            disabled={templateBatchLoading}
            loading={retryingTemplateId === row.enterpriseId}
            onClick={() => retryTemplateMigration(row.enterpriseId)}
          >
            Retry
          </Button>
        );
      }
    }
  ];

  const journeyTableColumns = [
    {
      title: 'Enterprise',
      dataIndex: 'enterpriseName',
      key: 'enterpriseName',
      sorter: (a, b) =>
        String(a.enterpriseName || '').localeCompare(
          String(b.enterpriseName || '')
        )
    },
    {
      title: 'Enterprise ID',
      dataIndex: 'enterpriseId',
      key: 'enterpriseId',
      width: 120,
      sorter: (a, b) => Number(a.enterpriseId) - Number(b.enterpriseId)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      sorter: (a, b) =>
        (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9),
      render: s => migrationStatusTag(s)
    },
    {
      title: 'Migrated at',
      dataIndex: 'migratedAt',
      key: 'migratedAt',
      width: 180,
      sorter: (a, b) => Number(a.migratedAt || 0) - Number(b.migratedAt || 0),
      render: ms => formatMs(ms)
    },
    {
      title: 'Details',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      ellipsis: true,
      render: function renderJourneyDetails(text, row) {
        if (row.status === 'error' && text) {
          return <span style={{ color: '#f5222d' }}>{text}</span>;
        }
        return '—';
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: function renderJourneyActions(_, row) {
        if (row.status !== 'error') {
          return '—';
        }
        return (
          <Button
            size="small"
            disabled={journeyBatchLoading}
            loading={retryingJourneyId === row.enterpriseId}
            onClick={() => retryJourneyMigration(row.enterpriseId)}
          >
            Retry
          </Button>
        );
      }
    }
  ];

  return (
    <div className="padding-20">
      <h2>Notifications Migration</h2>

      <Tabs
        activeKey={activeTab}
        onChange={key => {
          setActiveTab(key);
          if (key === 'history') {
            fetchMigrationLogs();
          }
        }}
        animated={false}
      >
        <TabPane tab="Run Migration" key="migrate">
          <p className="margin-bottom-20" style={{ color: 'rgba(0,0,0,0.45)' }}>
            Move one or more enterprises from legacy notifications to smart
            communications. You must add the smart communications service for{' '}
            <strong>every</strong> selected enterprise before continuing. Use{' '}
            <strong>Retry</strong> on template and journey steps if an API call
            fails. Open the <strong>Migration History</strong> tab to review.
          </p>

          <Steps current={current} size="small" className="margin-bottom-20">
            {steps.map((item, index) => (
              <Step
                key={item.title}
                title={
                  index === 0 && enterpriseSummaryLabel
                    ? `${item.title} (${enterpriseSummaryLabel})`
                    : item.title
                }
              />
            ))}
          </Steps>

          {current === 0 && (
            <Card title="Step 1 — Enterprises and Django Admin">
              {enterprise.isFetching ? (
                <Spin
                  indicator={
                    <Icon type="loading" style={{ fontSize: 24 }} spin />
                  }
                />
              ) : (
                <>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <div className="margin-bottom-8">Enterprises</div>
                      <Select
                        id="notifications-migration-enterprise"
                        mode="multiple"
                        showSearch
                        style={{ width: '100%', maxWidth: 720 }}
                        placeholder="Select one or more enterprises"
                        optionFilterProp="children"
                        value={selectedEnterpriseIds}
                        onChange={handleEnterpriseChange}
                        allowClear
                        filterOption={(input, option) =>
                          String(option.props.children)
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {enterprise.list.map((ent, i) => (
                          <Option key={i} value={ent.id}>
                            {ent.name} (ID: {ent.id})
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col span={24}>
                      <Alert
                        type="warning"
                        showIcon
                        message="Service ID required for every selected enterprise"
                        description={
                          <div>
                            <p>
                              Legacy notifications cannot be migrated to smart
                              notifications unless{' '}
                              <strong>service ID {SERVICE_ID}</strong> (smart
                              communications) is added in Django admin for{' '}
                              <strong>each</strong> enterprise you select. If
                              even one enterprise is missing this service,
                              template and journey migration may fail for that
                              tenant.
                            </p>
                            <p className="margin-bottom-0">
                              In Django admin, open{' '}
                              <strong>
                                <a
                                  href="https://cp-staging2.clickpost.in/adminsignup/enterpriseservice/"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Enterprise Service
                                </a>
                              </strong>{' '}
                              and add service ID {SERVICE_ID} for every selected
                              enterprise before running the migration APIs.
                            </p>
                          </div>
                        }
                      />
                    </Col>
                    <Col span={24}>
                      <Alert
                        type="info"
                        showIcon
                        message="Selected enterprises"
                        description={
                          selectedEnterpriseIds.length ? (
                            <ul className="margin-bottom-0">
                              {selectedEnterpriseIds.map(id => (
                                <li key={id}>
                                  {enterpriseById.get(id)?.name || 'Unknown'}{' '}
                                  (ID: {id})
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="margin-bottom-0">
                              None selected yet.
                            </span>
                          )
                        }
                      />
                    </Col>
                    <Col span={24}>
                      <Checkbox
                        checked={prerequisiteAck}
                        onChange={e => setPrerequisiteAck(e.target.checked)}
                        disabled={!selectedEnterpriseIds.length}
                      >
                        I have added service ID {SERVICE_ID} in Enterprise
                        Service (Django admin) for <strong>all</strong> selected
                        enterprises.
                      </Checkbox>
                    </Col>
                  </Row>
                  <div className="steps-action margin-top-20">
                    <Button type="primary" onClick={nextFromPrerequisite}>
                      Next
                    </Button>
                  </div>
                </>
              )}
            </Card>
          )}

          {current === 1 && (
            <Card title="Step 2 — Template migration">
              <p>
                Enterprises: <strong>{enterpriseSummaryLabel}</strong>
              </p>
              <p>
                Runs Use <strong>Run all</strong> to migrate every selected row,
                then <strong>Retry</strong> on any failure. Sort the table by
                Status to group failures. You may continue to journey migration
                even if some rows failed (you will be asked to confirm).
              </p>
              <div className="margin-bottom-16">
                <Button
                  type="primary"
                  onClick={runTemplateMigrationAll}
                  loading={templateBatchLoading}
                  disabled={!selectedEnterpriseIds.length}
                >
                  Run template migration (all)
                </Button>
              </div>
              <Table
                rowKey="enterpriseId"
                size="small"
                dataSource={templateRows}
                columns={templateTableColumns}
                pagination={false}
                locale={{ emptyText: 'No enterprises in this batch' }}
              />
              <div className="steps-action margin-top-16">
                <Button onClick={prev}>Previous</Button>
                <Button
                  type="primary"
                  style={{ marginLeft: 8 }}
                  onClick={goNextFromTemplate}
                  disabled={templateBatchLoading}
                >
                  Next
                </Button>
              </div>
              {templateHasFailures && (
                <Alert
                  className="margin-top-16"
                  type="warning"
                  showIcon
                  message="Some template migrations failed"
                  description="Fix the underlying issue (for example missing service ID 137), use Retry, or sort by Status to find failed rows. You can still continue to journey migration after confirming the risk."
                />
              )}
            </Card>
          )}

          {current === 2 && (
            <Card title="Step 3 — Journey migration">
              <p>
                Enterprises: <strong>{enterpriseSummaryLabel}</strong>
              </p>
              <p>
                Use <strong>Run all</strong> and <strong>Retry</strong> the same
                way as template migration. Sort by Status to inspect failures
                per enterprise.
              </p>
              <div className="margin-bottom-16">
                <Button
                  type="primary"
                  onClick={runJourneyMigrationAll}
                  loading={journeyBatchLoading}
                  disabled={!selectedEnterpriseIds.length}
                >
                  Run journey migration (all)
                </Button>
              </div>
              <Table
                rowKey="enterpriseId"
                size="small"
                dataSource={journeyRows}
                columns={journeyTableColumns}
                pagination={false}
                locale={{ emptyText: 'No enterprises in this batch' }}
              />
              <div className="steps-action margin-top-20">
                <Button onClick={prev}>Previous</Button>
                <Button
                  type="primary"
                  style={{ marginLeft: 8 }}
                  onClick={goFinishFromJourney}
                  disabled={journeyBatchLoading}
                  loading={finishPersistLoading}
                >
                  Finish
                </Button>
              </div>
              {journeyHasFailures && (
                <Alert
                  className="margin-top-16"
                  type="warning"
                  showIcon
                  message="Some journey migrations failed"
                  description="Use Retry per row or sort by Status. You can finish the wizard anyway after confirming; enterprises that failed will not be fully migrated."
                />
              )}
            </Card>
          )}

          {current === 3 && (
            <Card title="Migration complete">
              <Alert
                type={journeyAllSucceeded ? 'success' : 'warning'}
                showIcon
                message={
                  journeyAllSucceeded
                    ? 'Smart notifications migration finished'
                    : 'Migration wizard finished with some failures'
                }
                description={
                  <div>
                    <p className="margin-bottom-12">
                      Batch: <strong>{enterpriseSummaryLabel}</strong>.
                      Template:{' '}
                      {templateAllSucceeded
                        ? 'all succeeded.'
                        : 'some rows may have failed — see step 2 if you need to retry.'}{' '}
                      Journey:{' '}
                      {journeyAllSucceeded
                        ? 'all succeeded.'
                        : 'some rows may have failed — use Retry on step 3 or Migration History.'}{' '}
                      A migration history row was written for each enterprise
                      when you clicked <strong>Finish</strong> (including
                      partial or failed steps — see the Migration History tab).
                    </p>
                    <Table
                      rowKey="enterpriseId"
                      size="small"
                      pagination={false}
                      dataSource={selectedEnterpriseIds.map(id => {
                        const tr = templateRows.find(
                          r => r.enterpriseId === id
                        );
                        const jr = journeyRows.find(r => r.enterpriseId === id);
                        const logged = finishLogResultById[id] === true;
                        return {
                          enterpriseId: id,
                          enterpriseName:
                            enterpriseById.get(id)?.name ||
                            tr?.enterpriseName ||
                            String(id),
                          template: tr?.status || '—',
                          journey: jr?.status || '—',
                          historyWrite: logged ? 'yes' : 'no'
                        };
                      })}
                      columns={[
                        {
                          title: 'Enterprise',
                          dataIndex: 'enterpriseName',
                          key: 'enterpriseName',
                          width: 180,
                          sorter: (a, b) =>
                            String(a.enterpriseName || '').localeCompare(
                              String(b.enterpriseName || '')
                            )
                        },
                        {
                          title: 'Enterprise ID',
                          dataIndex: 'enterpriseId',
                          key: 'enterpriseId',
                          width: 120
                        },
                        {
                          title: 'Template',
                          dataIndex: 'template',
                          key: 'template',
                          width: 100,
                          render: s => migrationStatusTag(s)
                        },
                        {
                          title: 'Journey',
                          dataIndex: 'journey',
                          key: 'journey',
                          width: 100,
                          render: s => migrationStatusTag(s)
                        }
                      ]}
                    />
                  </div>
                }
              />
              <div className="steps-action margin-top-20">
                <Button onClick={prev}>Previous</Button>
                <Button
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    setCurrent(0);
                    setSelectedEnterpriseIds([]);
                    setPrerequisiteAck(false);
                    setTemplateRows([]);
                    setJourneyRows([]);
                    setFinishLogResultById({});
                    fetchMigrationLogs();
                  }}
                >
                  Start another batch
                </Button>
              </div>
            </Card>
          )}
        </TabPane>

        <TabPane tab="Migration History" key="history">
          <p className="margin-bottom-16" style={{ color: 'rgba(0,0,0,0.45)' }}>
            Log entries from the migration wizard, including partial runs. Use
            refresh to load the latest rows. Column headers are sortable.
          </p>
          <Card
            title="Migration history"
            extra={
              <Button
                size="small"
                onClick={fetchMigrationLogs}
                disabled={migrationLogs.isFetching}
              >
                <Icon type="reload" /> Refresh
              </Button>
            }
          >
            <Table
              rowKey="id"
              size="small"
              loading={migrationLogs.isFetching}
              dataSource={migrationLogs.list}
              columns={migrationLogColumns}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              locale={{
                emptyText: migrationLogs.isFetching
                  ? 'Loading…'
                  : 'No migration history yet. Click Finish on the Run migration tab to record this batch.'
              }}
              scroll={{ x: 1100 }}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default NotificationsMigration;
